import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNotifier } from '../components/Notifier';
import { useNotificationSettings } from '../stores/notificationSettingStore';
import { useLanguage } from '../utils/i18n';

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  const R = 6371; // km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function useVehicleNotifications(vehicleList) {
  const notify = useNotifier();
  const notifiedVehicles = useRef(new Set());
  const { settings: notifSettings } = useNotificationSettings();
  const { t } = useLanguage();
  const [loadedStatusMap, setLoadedStatusMap] = useState({});

  useEffect(() => {
    if (!vehicleList || vehicleList.length === 0) return;

    const currentViolations = new Set();
    const newLoadedStatusMap = {};
    const activeRitaseStr = localStorage.getItem('active_ritase_data');
    let activeRitaseMap = {};
    try {
      if (activeRitaseStr) activeRitaseMap = JSON.parse(activeRitaseStr).data || {};
    } catch (e) {
      console.warn('Failed to parse active_ritase_data', e);
    }

    const vehicleRoutesStr = localStorage.getItem('vehicle_routes');
    let vehicleRoutes = {};
    try {
      if (vehicleRoutesStr) vehicleRoutes = JSON.parse(vehicleRoutesStr);
    } catch {}

    const trackingStateStr = localStorage.getItem('vehicle_tracking_state');
    let trackingState = {};
    try {
      if (trackingStateStr) trackingState = JSON.parse(trackingStateStr);
    } catch {}

    let hasStateChanges = false;
    // console.log('Raw', vehicleList);

    vehicleList.forEach((v) => {
      const rawData = v.raw || v;
      const rawTime = rawData.last_update;
      if (rawTime && !dayjs(rawTime).isSame(dayjs(), 'day')) return;
      const plate = rawData.vehicle_license_plate || rawData.license_plate || 'Unknown';
      const vId = rawData.vehicle_id || v.vehicle_id;
      const formattedTime = rawTime
        ? dayjs(rawTime).format('DD MMM YYYY HH:mm:ss')
        : t('mapsTracking.unknownTime', 'Waktu tidak diketahui');

      let isLoadedRitase = false;
      let ritaseEnd = 0;
      const checkTime = rawTime ? dayjs(rawTime).valueOf() : dayjs().valueOf();

      // 1. Lapis 1: API Ritase
      if (vId && activeRitaseMap[vId]) {
        for (const trip of activeRitaseMap[vId]) {
          const start = trip.start ? dayjs(trip.start).valueOf() : 0;
          const pureEnd = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).valueOf() : Infinity;
          const endGrace = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).add(30, 'minute').valueOf() : Infinity;

          if (start > 0 && checkTime >= start && checkTime <= endGrace) {
            if (checkTime <= pureEnd) {
              isLoadedRitase = true;
            }
            if (pureEnd !== Infinity && pureEnd > ritaseEnd) ritaseEnd = pureEnd;
          }
        }
      }

      const spatialId = Number(rawData.spatial_id || v.spatial_id || rawData.info?.spatial_id || 0);
      const lat = Number(rawData.lat || rawData.latitude || v.lat || 0);
      const lon = Number(rawData.lon || rawData.longitude || v.lon || 0);
      const speed = Number(rawData.speed || v.speed || 0);
      const engine = String(rawData.engine || v.engine || '').toUpperCase();

      if (!trackingState[vId]) {
        trackingState[vId] = { checkpoint_status: null, checkpoint_time: 0, history: [] };
      }
      const vState = trackingState[vId];

      let routes = vehicleRoutes[vId];
      if (routes && !Array.isArray(routes)) {
        routes = [routes];
      }

      // 2. Lapis 2: Dynamic Checkpoint
      if (routes && routes.length > 0) {
        let isDest = false;
        let isOrigin = false;
        for (const route of routes) {
          if (spatialId > 0 && spatialId === Number(route.dest_id)) isDest = true;
          if (spatialId > 0 && spatialId === Number(route.origin_id)) isOrigin = true;
        }
        if (isDest) {
          vState.checkpoint_status = 'EMPTY';
          vState.checkpoint_time = checkTime;
          hasStateChanges = true;
        } else if (isOrigin) {
          vState.checkpoint_status = 'LOADED';
          vState.checkpoint_time = checkTime;
          hasStateChanges = true;
        }
      }

      let isLoadedCheckpoint = false;
      if (vState.checkpoint_status && vState.checkpoint_time >= ritaseEnd) {
        isLoadedCheckpoint = vState.checkpoint_status === 'LOADED';
      }

      // 3. Lapis 3: Haversine Heuristic 5 Menit
      let isLoadedHeuristic = false;
      if (routes && routes.length > 0 && engine === 'ON' && speed > 0 && lat !== 0 && lon !== 0) {
        vState.history.push({
          time: checkTime,
          lat,
          lon,
          dist_orig: calculateDistance(lat, lon, routes[0].origin_lat, routes[0].origin_lon),
          dist_dest: calculateDistance(lat, lon, routes[0].dest_lat, routes[0].dest_lon)
        });

        // Simpan 5 menit (300000 ms) terakhir
        const fiveMinsAgo = checkTime - 300000;
        vState.history = vState.history.filter((h) => h.time >= fiveMinsAgo);
        hasStateChanges = true;

        if (vState.history.length > 1) {
          const oldest = vState.history[0];
          const newest = vState.history[vState.history.length - 1];

          for (const route of routes) {
            if (oldest.lat !== undefined && newest.lat !== undefined) {
              const oldDistOrig = calculateDistance(oldest.lat, oldest.lon, route.origin_lat, route.origin_lon);
              const oldDistDest = calculateDistance(oldest.lat, oldest.lon, route.dest_lat, route.dest_lon);
              const newDistOrig = calculateDistance(newest.lat, newest.lon, route.origin_lat, route.origin_lon);
              const newDistDest = calculateDistance(newest.lat, newest.lon, route.dest_lat, route.dest_lon);

              const deltaDest = oldDistDest - newDistDest;
              const deltaOrig = oldDistOrig - newDistOrig;

              if (deltaDest > 0.05 && deltaOrig < -0.05) {
                isLoadedHeuristic = true;
                break;
              }
            } else if (oldest.dist_orig !== undefined && newest.dist_orig !== undefined) {
              // Backward compatibility
              const deltaDest = oldest.dist_dest - newest.dist_dest;
              const deltaOrig = oldest.dist_orig - newest.dist_orig;
              if (deltaDest > deltaOrig && deltaDest > 0.05) {
                isLoadedHeuristic = true;
                break;
              }
            }
          }
        }
      }

      // Kombinasi ketiga lapis (OR)
      const isLoaded = isLoadedRitase || isLoadedCheckpoint || isLoadedHeuristic;

      vState.is_loaded = isLoaded;
      newLoadedStatusMap[vId] = isLoaded;

      const speedMessage = String(v.raw?.speed_message || v.speed_message || rawData.info?.speed_message || '');
      const msgLower = speedMessage.toLowerCase();
      const eventName = String(v.raw?.event || v.event || rawData.alarm_type || '').toLowerCase();
      const harshEvent = String(v.raw?.harsh_event || v.harsh_event || rawData.info?.harsh_event || '').toLowerCase();

      const isHarshBraking =
        harshEvent.includes('braking') ||
        harshEvent.includes('breaking') ||
        msgLower.includes('harsh braking') ||
        eventName.includes('harsh braking');
      const isHarshAccel =
        harshEvent.includes('acceleration') || msgLower.includes('harsh acceleration') || eventName.includes('harsh acceleration');
      const isHarshCorner =
        harshEvent.includes('cornering') || msgLower.includes('harsh cornering') || eventName.includes('harsh cornering');

      // 1. PRIORITAS HARSH EVENTS (Dieksekusi Pertama)
      const locName = v.raw?.location || v.location || rawData.polygon_type_name || 'Unknown Location';
      const timeStr = rawTime ? dayjs(rawTime).format('DD MMM YYYY HH:mm:ss') : 'Unknown Time';

      if (isHarshBraking && notifSettings.harshBraking) {
        const violationKey = `${plate}-HarshBraking`;
        currentViolations.add(violationKey);
        if (!notifiedVehicles.current.has(violationKey)) {
          notify({ message: `${plate}: Harsh Braking detected! Location: ${locName}. Time: ${timeStr}.`, severity: 'error' });
          notifiedVehicles.current.add(violationKey);
        }
      } else if (isHarshAccel && notifSettings.harshAcceleration) {
        const violationKey = `${plate}-HarshAccel`;
        currentViolations.add(violationKey);
        if (!notifiedVehicles.current.has(violationKey)) {
          notify({ message: `${plate}: Harsh Acceleration detected! Location: ${locName}. Time: ${timeStr}.`, severity: 'error' });
          notifiedVehicles.current.add(violationKey);
        }
      } else if (isHarshCorner && notifSettings.harshCornering) {
        const violationKey = `${plate}-HarshCorner`;
        currentViolations.add(violationKey);
        if (!notifiedVehicles.current.has(violationKey)) {
          notify({ message: `${plate}: Harsh Cornering detected! Location: ${locName}. Time: ${timeStr}.`, severity: 'error' });
          notifiedVehicles.current.add(violationKey);
        }
      }

      // 2. PRIORITAS SPEED EVENTS (Dipisah dari blok else Harsh, dieksekusi setelah Harsh)
      const spatialMaxSpeed = Number(v.raw?.spatial_max_speed || v.spatial_max_speed || rawData.info?.spatial_max_speed || 0);
      const spatialMinSpeed = Number(v.raw?.spatial_min_speed || v.spatial_min_speed || rawData.info?.spatial_min_speed || 0);
      // const speed = Number(v.raw?.speed || v.speed || rawData.info?.speed || 0);

      const isOverSpeedSpatial = spatialMaxSpeed > 0 && speed > spatialMaxSpeed && isLoaded;
      const isUnderSpeedSpatial = spatialMinSpeed > 0 && speed > 0 && speed < spatialMinSpeed && isLoaded;

      const isOverSpeedMessage = msgLower.includes('over') || eventName.includes('overspeed');
      // const isUnderSpeedMessage = msgLower.includes('under') || eventName.includes('underspeed');
      const limitSpeed = Number(v.vehicle?.max_speed || rawData.info?.max_speed || 70);
      const engineStatus = String(
        v.raw?.engine || v.engine || v.raw?.engine_status || v.engine_status || rawData.info?.engine_status || ''
      ).toUpperCase();
      const isOverSpeedLimit = engineStatus === 'ON' && speed > limitSpeed;
      const isOverSpeed = isOverSpeedMessage || isOverSpeedLimit;

      if (isOverSpeedSpatial) {
        const violationKey = `${plate}-OverSpeedSpatial-${locName}`;
        currentViolations.add(violationKey);

        if (!notifiedVehicles.current.has(violationKey)) {
          notify({
            message: `${plate}: Spatial Over Speed! ${speed} km/h (Limit: ${spatialMaxSpeed} km/h). Location: ${locName}. Time: ${timeStr}.`,
            severity: 'error'
          });
          notifiedVehicles.current.add(violationKey);
        }
      } else if (isUnderSpeedSpatial) {
        const violationKey = `${plate}-UnderSpeedSpatial-${locName}`;
        currentViolations.add(violationKey);

        if (!notifiedVehicles.current.has(violationKey)) {
          notify({
            message: `${plate}: Spatial Under Speed! ${speed} km/h (Min Limit: ${spatialMinSpeed} km/h). Location: ${locName}. Time: ${timeStr}.`,
            severity: 'warning'
          });
          notifiedVehicles.current.add(violationKey);
        }
      } else if (isOverSpeed && notifSettings.overSpeed) {
        const violationKey = `${plate}-OverSpeed`;
        currentViolations.add(violationKey);

        if (!notifiedVehicles.current.has(violationKey)) {
          notify({
            message: `${plate}: Over Speed! ${speed} km/h (Limit: ${limitSpeed} km/h). Location: ${locName}. Time: ${timeStr}.`,
            severity: 'error'
          });
          notifiedVehicles.current.add(violationKey);
        }
      }
      // else if (isUnderSpeedMessage) {
      //   const violationKey = `${plate}-UnderSpeed`;
      //   currentViolations.add(violationKey);

      //   if (!notifiedVehicles.current.has(violationKey)) {
      //     notify({
      //       message: `${plate}: Under Speed!`,
      //       severity: 'warning'
      //     });
      //     notifiedVehicles.current.add(violationKey);
      //   }
      // }

      // 3. PRIORITAS ENTER / EXIT LOCATION (Dieksekusi setelah Speed)
      const isEnter =
        rawData.enter_location === true || String(rawData.enter_location).toLowerCase() === 'true' || rawData.enter_location === 1;
      if (isEnter) {
        const locationName = rawData.location || rawData.polygon_type_name || t('mapsTracking.unknownLocation', 'Unknown Location');
        const locationType = String(rawData.location_type || rawData.info?.location_type || '').toUpperCase();

        let shouldNotifyEnter = false;

        if (locationType.includes('PIT') || locationType.includes('POOL')) {
          shouldNotifyEnter = notifSettings.enterPool;
        } else if (locationType.includes('JETTY') || locationType.includes('CUSTOMER')) {
          shouldNotifyEnter = notifSettings.enterCustomerPoint;
        } else if (locationType.includes('GEOFENCE')) {
          shouldNotifyEnter = notifSettings.enterGeofence;
        } else {
          shouldNotifyEnter = notifSettings.enterGeofence;
        }

        // console.log(`[NOTIF DEBUG] Enter ${plate} di ${locationName}. Type: ${locationType}. shouldNotifyEnter: ${shouldNotifyEnter}`);

        if (shouldNotifyEnter) {
          const locationKey = `${plate}-EnterLocation-${locationName}`;
          currentViolations.add(locationKey);

          if (!notifiedVehicles.current.has(locationKey)) {
            notify({
              message: `${plate} ${t('mapsTracking.enterLocationNotif', 'Enter location')} ${locationName} ${t('mapsTracking.atTime', 'pada')} ${formattedTime}`,
              severity: 'info'
            });
            notifiedVehicles.current.add(locationKey);
          }
        }
      }

      const isExit =
        rawData.exit_location === true || String(rawData.exit_location).toLowerCase() === 'true' || rawData.exit_location === 1;
      if (isExit) {
        const locationName = rawData.location || rawData.polygon_type_name || t('mapsTracking.unknownLocation', 'Unknown Location');
        const locationType = String(rawData.location_type || rawData.info?.location_type || '').toUpperCase();

        let shouldNotifyExit = false;

        if (locationType.includes('PIT') || locationType.includes('POOL')) {
          shouldNotifyExit = notifSettings.exitPool;
        } else if (locationType.includes('JETTY') || locationType.includes('CUSTOMER')) {
          shouldNotifyExit = notifSettings.exitCustomerPoint;
        } else if (locationType.includes('GEOFENCE')) {
          shouldNotifyExit = notifSettings.exitGeofence;
        } else {
          shouldNotifyExit = notifSettings.exitGeofence;
        }

        // console.log(`[NOTIF DEBUG] Exit ${plate} dari ${locationName}. Type: ${locationType}. shouldNotifyExit: ${shouldNotifyExit}`);

        if (shouldNotifyExit) {
          const locationKey = `${plate}-ExitLocation-${locationName}`;
          currentViolations.add(locationKey);

          if (!notifiedVehicles.current.has(locationKey)) {
            notify({
              message: `${plate} ${t('mapsTracking.exitLocationNotif', 'Exit location')} ${locationName} ${t('mapsTracking.atTime', 'pada')} ${formattedTime}`,
              severity: 'warning'
            });
            notifiedVehicles.current.add(locationKey);
          }
        }
      }

      if (eventName.includes('panic') || eventName.includes('sos')) {
        if (notifSettings.panicButton) {
          const violationKey = `${plate}-Panic`;
          currentViolations.add(violationKey);
          if (!notifiedVehicles.current.has(violationKey)) {
            notify({
              message: `${t('mapsTracking.panicNotif', '🚨 DARURAT: Panic Button ditekan pada')} ${plate} ${t('mapsTracking.atLocation', 'di')} ${rawData.location}`,
              severity: 'error'
            });
            notifiedVehicles.current.add(violationKey);
          }
        }
      }

      const vehicleStatus = String(rawData.status || rawData.info?.status || '').toLowerCase();
      if (vehicleStatus.includes('delay')) {
        const violationKey = `${plate}-Delay`;
        currentViolations.add(violationKey);

        if (!notifiedVehicles.current.has(violationKey)) {
          let timeAgoStr = '';
          if (rawTime) {
            const updateTime = dayjs(rawTime);
            const now = dayjs();
            const diffMin = now.diff(updateTime, 'minute');
            const diffSec = now.diff(updateTime, 'second') % 60;
            const days = Math.floor(diffMin / (24 * 60));
            const hours = Math.floor((diffMin % (24 * 60)) / 60);
            const mins = diffMin % 60;

            const parts = [];
            if (days > 0) parts.push(`${days}d`);
            if (hours > 0) parts.push(`${hours}h`);
            if (mins > 0) parts.push(`${mins}m`);
            if (parts.length === 0) parts.push(`${diffSec}s`);
            timeAgoStr = parts.join(' ') + ' ago';
          }

          const delaySeverity = vehicleStatus.includes('24') ? 'warning' : 'warning';
          const originalStatus = rawData.status || rawData.info?.status || 'delay';
          const cleanTime = rawTime ? dayjs(rawTime).format('DD MMM, HH:mm') : 'Unknown';

          notify({
            message: `${plate}: ${originalStatus} at ${cleanTime} (${timeAgoStr})`,
            severity: delaySeverity
          });
          notifiedVehicles.current.add(violationKey);
        }
      }
    });

    if (hasStateChanges) {
      localStorage.setItem('vehicle_tracking_state', JSON.stringify(trackingState));
    }

    notifiedVehicles.current.forEach((key) => {
      if (!currentViolations.has(key)) {
        notifiedVehicles.current.delete(key);
      }
    });

    setLoadedStatusMap(newLoadedStatusMap);
  }, [vehicleList, notify, notifSettings, t]);

  return loadedStatusMap;
}
